import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { SelectedAttributeService } from '../../../services/selected-attribute.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { CheckoutService } from '../../../services/checkout.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { UnavailableDialogComponent } from '../../unavailable.dialog/unavailable.dialog.component';
import { Design } from 'src/app/interfaces/design';
import { DesignCategory } from 'src/app/interfaces/design-category';
import { StyleImagesProviderService } from 'src/app/providers/style-images/style-images.provider.service';
import { StyleImageGalleryComponent } from '../../style-image-gallery/style-image-gallery.component';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { TopBarService } from 'src/app/services/top-bar-service/top-bar-service.service';

@Component({
  selector: 'app-design-explorer',
  templateUrl: './design-explorer.component.html',
  styleUrl: './design-explorer.component.scss'
})
export class DesignExplorerComponent {
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private designsService = inject(DesignsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private rulesService = inject(RulesService);
  private checkoutService = inject(CheckoutService);
  private loadingService = inject(LoadingService);
  private styleImagesProvider = inject(StyleImagesProviderService);
  private topBarService = inject(TopBarService);
  private dialog = inject(MatDialog);

  designs = computed(() => this.designsService.values());
  selectedDesign = computed(() => this.selectedAttributeService.getValue().design);
  designsWithStyleImages = signal<number[]>([]);
  availableDesigns = signal<number[]>([]);
  reachableDesigns = signal<number[]>([]);
  filteredDesigns = signal<Design[]>([]);
  designsToShow = computed(() => {
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableDesigns = this.reachableDesigns();
    const filteredDesigns = this.filteredDesigns();
    const designs = this.designs();
    const designsToUse = filteredDesigns.length > 0 ? filteredDesigns : designs;
    if (!isShowingUnavailable) {
      const selectedDesignCode = this.selectedDesign()?.shortName;
      return designsToUse.filter(d => reachableDesigns.includes(d.id) || d.shortName === selectedDesignCode);
    }

    return designsToUse;
  });
  filteredCategories = signal<DesignCategory[]>([]);
  categories = computed(() => this.designsService.categories());
  categoriesToShow = computed(() => {
      const designsToShow = this.designsToShow();
      const filteredCategories = this.filteredCategories();
      const categoriesToShow = (filteredCategories.length > 0 ? filteredCategories : this.categories()).filter(c => designsToShow.some(d => d.categories.includes(c.id)));
      return categoriesToShow;
  });
  expandedCategories = signal(new Map<number, boolean>(), { equal: () => false });

  constructor() {
    effect(async () => {
      this.loadingService.load('DesignComponent::GetReachable');
      const reachableAttributes = await this.rulesService.getReachableDesigns(this.selectedAttributeService.configuredItem(), untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
      this.reachableDesigns.set(reachableAttributes);
      this.loadingService.done('DesignComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      this.loadingService.load('DesignComponent::GetAvailable');
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableDesigns.set(availableAttributes.designIDs);
      this.loadingService.done('DesignComponent::GetAvailable');
    }, { allowSignalWrites: true });

    effect(async () => {
      this.loadingService.load('DesignExplorerComponent::GetDesignsWithStyleImages');
      const designIDs = await this.styleImagesProvider.getAvailableDesigns(this.selectedAttributeService.configuredItem());
      this.designsWithStyleImages.set(designIDs);
      this.loadingService.done('DesignExplorerComponent::GetDesignsWithStyleImages');
    }, { allowSignalWrites: true });
  }

  getDesignsInCategory(id: number) {
    return this.designsToShow().filter(d => d.categories.includes(id));
  }

  async viewExamples(design: Design) {
    this.loadingService.load('DesignExplorerComponent::ViewExamples');
    const configuredItem = { ...this.selectedAttributeService.configuredItem(), design };
    const images = await this.styleImagesProvider.getP4s(configuredItem);
    this.loadingService.done('DesignExplorerComponent::ViewExamples');
    this.dialog.open(StyleImageGalleryComponent, {
      data: { title: design.displayName, selectedImage: images[0], images },
      panelClass: 'GalleryGlobal',
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
      autoFocus: false
    });
  }

  async designClick(design: Design | null) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'design_explorer', 'design', design?.shortName);
    }

    if (design) {
      if (this.availableDesigns().includes(design.id)) {
        if (this.selectedDesign()?.displayName !== design?.displayName) {
          this.loadingService.load('DesignExplorerComponent::MutateDesign');
          await this.selectedAttributeService.mutateDesign(design);
          this.loadingService.done('DesignExplorerComponent::MutateDesign');
        }
      } else {
        try {
          this.loadingService.load('DesignExplorerComponent::SetClosestDesign');
          await this.selectedAttributeService.setClosestByDesign(design);
        } catch (e) {
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'design' });
        } finally {
          this.loadingService.done('DesignExplorerComponent::SetClosestDesign');
        }
      }
    }
    this.topBarService.selectActiveItem('customize');
  }
}
